import React, { useRef, useEffect } from 'react';
import { ChartConfig } from '../types';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ScatterChart, Scatter } from 'recharts';

import { MapChart } from '../src/charts/MapChart';
import { ForceGraphChart } from '../src/charts/ForceGraph/ForceGraphChart';
import { ThreeDBarChart } from '../src/charts/ThreeDBarChart/ThreeDBarChart';
import { ScientificChart } from '../src/charts/Scientific';
import { ScientificVisuals } from './ScientificVisuals'; // Legacy fallback

interface AdvancedVisualsProps {
    config: ChartConfig;
    data: any[];
    isVisible: boolean;
    canvasRef?: React.RefObject<HTMLCanvasElement>;
}

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
    <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
            <p className="mb-2 text-amber-500 font-bold">Configuration Required</p>
            <p className="text-sm">{message}</p>
        </div>
    </div>
);

// --- Vector Chart ---
const VectorChart: React.FC<{ config: ChartConfig, data: any[] }> = ({ config, data }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const vec = config.advanced?.vector;

    useEffect(() => {
        const canvas = canvasRef.current;
        // Check all conditions here inside the effect, instead of returning early from the component
        if (!canvas || !config.advanced || !vec || !vec.uField || !vec.vField || !data || data.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);

        // Filter valid data points
        const validData = data.filter(d =>
            d &&
            typeof d.x === 'number' && !isNaN(d.x) &&
            typeof d.y === 'number' && !isNaN(d.y) &&
            typeof d.u === 'number' && !isNaN(d.u) &&
            typeof d.v === 'number' && !isNaN(d.v)
        );

        if (validData.length === 0) {
            // Draw "No valid data" message on canvas
            ctx.fillStyle = '#888';
            ctx.font = '16px sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('No valid vector data to display', width / 2, height / 2);
            return;
        }

        const xs = validData.map(d => d.x);
        const ys = validData.map(d => d.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const rangeX = maxX - minX || 1;
        const rangeY = maxY - minY || 1;

        const scaleX = (width - 40) / rangeX;
        const scaleY = (height - 40) / rangeY;

        const arrowScale = vec.arrowScaling || 1;
        const normalize = vec.normalize;

        ctx.strokeStyle = config.customization?.color || '#10b981';
        ctx.lineWidth = 1;

        const drawArrow = (ctx: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number) => {
            const headlen = 5;
            const dx = toX - fromX;
            const dy = toY - fromY;
            const angle = Math.atan2(dy, dx);
            ctx.beginPath();
            ctx.moveTo(fromX, fromY);
            ctx.lineTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle - Math.PI / 6), toY - headlen * Math.sin(angle - Math.PI / 6));
            ctx.moveTo(toX, toY);
            ctx.lineTo(toX - headlen * Math.cos(angle + Math.PI / 6), toY - headlen * Math.sin(angle + Math.PI / 6));
            ctx.stroke();
        };

        validData.forEach(d => {
            let u = d.u;
            let v = d.v;

            if (normalize) {
                const mag = Math.sqrt(u * u + v * v) || 1;
                u /= mag;
                v /= mag;
            }

            const px = 20 + (d.x - minX) * scaleX;
            const py = height - 20 - (d.y - minY) * scaleY;

            const toX = px + u * arrowScale * 10;
            const toY = py - v * arrowScale * 10;

            drawArrow(ctx, px, py, toX, toY);
        });

    }, [config, data, vec]);

    // Early return with error message if configuration is invalid
    if (!config.advanced || !vec) {
        return <ErrorMessage message="Vector chart configuration is missing. Please configure advanced settings." />;
    }

    if (!vec.uField || !vec.vField) {
        return <ErrorMessage message="Select U and V fields to render vector field." />;
    }

    // Check if data is valid
    if (!data || !Array.isArray(data) || data.length === 0) {
        return <ErrorMessage message="No data available. Please check your data source and field mappings." />;
    }

    return <canvas ref={canvasRef} width={800} height={600} className="w-full h-full object-contain" />;
};

// --- 3D Chart ---
const ThreeDChart: React.FC<{ config: ChartConfig, data: any[] }> = ({ config, data }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const td = config.advanced?.threeD;

    useEffect(() => {
        const canvas = canvasRef.current;
        // Check conditions inside effect
        if (!canvas || !td || !td.xField || !td.yField || !td.zField || data.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const width = canvas.width;
        const height = canvas.height;
        ctx.clearRect(0, 0, width, height);

        const xs = data.map(d => d.x);
        const ys = data.map(d => d.y);
        const zs = data.map(d => d.z || 0);

        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const minZ = Math.min(...zs), maxZ = Math.max(...zs);

        const rangeX = maxX - minX || 1;
        const rangeY = maxY - minY || 1;
        const rangeZ = maxZ - minZ || 1;

        const azimuth = 45;
        const elevation = 30;
        const radAzimuth = (azimuth * Math.PI) / 180;
        const radElevation = (elevation * Math.PI) / 180;

        const cosAz = Math.cos(radAzimuth);
        const sinAz = Math.sin(radAzimuth);
        const cosEl = Math.cos(radElevation);
        const sinEl = Math.sin(radElevation);

        const project = (x: number, y: number, z: number) => {
            const nx = ((x - minX) / rangeX) * 2 - 1;
            const ny = ((y - minY) / rangeY) * 2 - 1;
            const nz = ((z - minZ) / rangeZ) * 2 - 1;

            const x1 = nx * cosAz - nz * sinAz;
            const z1 = nx * sinAz + nz * cosAz;

            const y2 = ny * cosEl - z1 * sinEl;
            const z2 = ny * sinEl + z1 * cosEl;

            const perspective = 2;
            const scale = Math.min(width, height) / 3;
            const factor = perspective / (perspective - z2 * 0.5);

            return {
                px: width / 2 + x1 * scale * factor,
                py: height / 2 - y2 * scale * factor,
                depth: z2
            };
        };

        const points = data.map(d => ({
            ...project(d.x, d.y, d.z || 0),
            color: config.customization?.color || '#4f46e5'
        }));

        points.sort((a, b) => b.depth - a.depth);

        points.forEach(p => {
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(p.px, p.py, td.pointSize || 3, 0, Math.PI * 2);
            ctx.fill();
        });

        ctx.fillStyle = '#888';
        ctx.font = '10px sans-serif';
        ctx.fillText(`X: ${td.xField}, Y: ${td.yField}, Z: ${td.zField}`, 10, height - 10);

    }, [config, data, td]);

    if (!td?.xField || !td?.yField || !td?.zField) {
        return <ErrorMessage message="Select X, Y, and Z fields to render 3D plot." />;
    }

    return <canvas ref={canvasRef} width={800} height={600} className="w-full h-full object-contain" />;
};

export const AdvancedVisuals: React.FC<AdvancedVisualsProps> = ({ config, data, isVisible }) => {
    if (!isVisible) return null;

    const { type } = config;

    if (type === 'scientific') {
        // Enhance scientific config with field mappings from top-level config
        const enhancedConfig = {
            ...config,
            advanced: {
                ...config.advanced,
                scientific: {
                    ...config.advanced?.scientific,
                    fieldMappings: {
                        ...config.advanced?.scientific?.fieldMappings,
                        // Fallbacks from top-level config for dashboard compatibility
                        xField: config.advanced?.scientific?.fieldMappings?.xField || config.xField,
                        yField: config.advanced?.scientific?.fieldMappings?.yField || config.yField || config.measures?.[0]?.field,
                        colorField: config.advanced?.scientific?.fieldMappings?.colorField || config.encoding?.color?.field,
                        sizeField: config.advanced?.scientific?.fieldMappings?.sizeField || config.sizeField
                    }
                }
            }
        };
        console.log('[AdvancedVisuals][scientific] Debug:', {
            originalXField: config.xField,
            originalYField: config.yField,
            scientificXField: config.advanced?.scientific?.fieldMappings?.xField,
            scientificYField: config.advanced?.scientific?.fieldMappings?.yField,
            enhancedXField: enhancedConfig.advanced?.scientific?.fieldMappings?.xField,
            enhancedYField: enhancedConfig.advanced?.scientific?.fieldMappings?.yField,
            dataLength: data?.length,
            sampleData: data?.[0]
        });
        return <ScientificChart config={enhancedConfig} data={data} isVisible={isVisible} />;
    }

    // Route regular 'vector' to ScientificChart with vector-field plotType for zoom/pan/palette
    if (type === 'vector') {
        const enhancedConfig = {
            ...config,
            advanced: {
                ...config.advanced,
                scientific: {
                    ...config.advanced?.scientific,
                    plotType: 'vector',
                    fieldMappings: {
                        xField: config.xField || 'x',
                        yField: config.yField || 'y',
                        uField: config.advanced?.vector?.uField || 'u',
                        vField: config.advanced?.vector?.vField || 'v',
                        ...config.advanced?.scientific?.fieldMappings
                    }
                }
            }
        };
        return <ScientificChart config={enhancedConfig} data={data} isVisible={isVisible} />;
    }

    if (type === '3d-bar') {
        return <ThreeDBarChart config={config} data={data} />;
    }

    // Route regular '3d' to ScientificChart with 3d-scatter plotType for zoom/pan/palette/rotation
    if (type === '3d') {
        const enhancedConfig = {
            ...config,
            advanced: {
                ...config.advanced,
                scientific: {
                    ...config.advanced?.scientific,
                    plotType: '3d-scatter',
                    fieldMappings: {
                        xField: config.advanced?.threeD?.xField || config.xField || 'x',
                        yField: config.advanced?.threeD?.yField || config.yField || 'y',
                        zField: config.advanced?.threeD?.zField || 'z',
                        colorField: config.advanced?.threeD?.colorField,
                        sizeField: config.advanced?.threeD?.sizeField,
                        ...config.advanced?.scientific?.fieldMappings
                    }
                }
            }
        };
        return <ScientificChart config={enhancedConfig} data={data} isVisible={isVisible} />;
    }

    // Route regular 'heatmap' to ScientificChart with heatmap plotType for palette/controls
    if (type === 'heatmap') {
        const enhancedConfig = {
            ...config,
            advanced: {
                ...config.advanced,
                scientific: {
                    // Start with existing scientific config
                    ...config.advanced?.scientific,
                    // Override plotType
                    plotType: 'heatmap',
                    // Build fieldMappings with heatmap-specific fields taking precedence
                    fieldMappings: {
                        ...config.advanced?.scientific?.fieldMappings,
                        // Heatmap-specific overrides come AFTER to take precedence
                        // Use keys from HeatmapMappingZone: xCategoryField, yCategoryField, valueField
                        // Fallback to scientific config if regular mappings are missing
                        xField: (config as any).xCategoryField || config.xField || config.advanced?.scientific?.fieldMappings?.xField || 'x',
                        yField: (config as any).yCategoryField || config.yField || config.advanced?.scientific?.fieldMappings?.yField || 'y',
                        colorField: (config as any).valueField || config.measures?.[0]?.field || config.advanced?.scientific?.fieldMappings?.colorField || 'value'
                    }
                }
            }
        };
        return <ScientificChart config={enhancedConfig} data={data} isVisible={isVisible} />;
    }

    if (type === 'map') {
        // Map data mapping from config to MapChart props
        const mapMapping = {
            latitudeField: config.advanced?.map?.latField,
            longitudeField: config.advanced?.map?.lonField,
            regionKeyField: config.advanced?.map?.regionKeyField,
            valueField: config.yField, // Default value field
            categoryField: config.encoding?.color?.field
        };

        const mapConfig = config.advanced?.mapConfig || {};

        return (
            <MapChart
                data={data}
                dataMapping={mapMapping}
                config={mapConfig}
                className="rounded-xl overflow-hidden border border-white/10"
            />
        );
    }

    if (type === 'force-directed') {
        return <ForceGraphChart config={config} data={data} />;
    }

    return <div className="text-center text-gray-500 mt-10">Unsupported Advanced Chart Type</div>;
};
